import { useQuery } from "@tanstack/react-query";
import { MessageSquareText } from "lucide-react";
import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";

import { Pagination } from "../../components/pagination";
import type {
  AdminChannel,
  AdminDialogsQuery,
  ChannelType,
} from "../../lib/api";
import { adminApi } from "../../lib/api";
import { useAdminAuth } from "../../lib/auth";
import { formatDateTime, formatTokens, localDateTimeToIso } from "../../lib/format";

const PAGE_SIZE = 50;

interface DialogFilterDraft {
  channelId: string;
  contact: string;
  from: string;
  to: string;
}

type AppliedDialogFilters = Omit<
  AdminDialogsQuery,
  "cursor" | "funnelId" | "take"
>;

const emptyFilters: DialogFilterDraft = {
  channelId: "",
  contact: "",
  from: "",
  to: "",
};

function channelTypeLabel(type: ChannelType): string {
  return type === "WHATSAPP" ? "WhatsApp" : "Telegram";
}

export function DialogsTab({
  accountId,
  funnelId,
  channels,
}: {
  accountId: string;
  funnelId: string;
  channels: AdminChannel[];
}) {
  const { token } = useAdminAuth();
  const [draft, setDraft] = useState<DialogFilterDraft>(emptyFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<AppliedDialogFilters>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const currentCursor = cursorStack[pageIndex] ?? null;

  const channelOptions = useMemo(() => {
    const options = new Map<string, ChannelType>();
    for (const channel of channels) {
      if (!options.has(channel.wazzupChannelId)) {
        options.set(channel.wazzupChannelId, channel.type);
      }
    }
    return Array.from(options, ([id, type]) => ({ id, type }));
  }, [channels]);

  const query = useMemo<AdminDialogsQuery>(
    () => ({
      funnelId,
      ...appliedFilters,
      ...(currentCursor ? { cursor: currentCursor } : {}),
      take: PAGE_SIZE,
    }),
    [appliedFilters, currentCursor, funnelId],
  );

  const dialogs = useQuery({
    queryKey: [
      "admin",
      "account",
      accountId,
      "funnel",
      funnelId,
      "dialogs",
      query,
    ],
    queryFn: () => adminApi.dialogs(token!, accountId, query),
    enabled: Boolean(token),
  });

  function resetPagination() {
    setCursorStack([null]);
    setPageIndex(0);
  }

  function handleApply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const from = draft.from ? localDateTimeToIso(draft.from) : null;
    const to = draft.to ? localDateTimeToIso(draft.to) : null;

    if (draft.from && !from) {
      setValidationError("Укажите корректную дату начала");
      return;
    }
    if (draft.to && !to) {
      setValidationError("Укажите корректную дату окончания");
      return;
    }
    if (from && to && from > to) {
      setValidationError("Дата начала не может быть позже даты окончания");
      return;
    }

    setValidationError(null);
    setAppliedFilters({
      ...(draft.channelId ? { channelId: draft.channelId } : {}),
      ...(draft.contact.trim() ? { contact: draft.contact.trim() } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    });
    resetPagination();
  }

  function handleReset() {
    setDraft(emptyFilters);
    setAppliedFilters({});
    setValidationError(null);
    resetPagination();
  }

  function handleForward() {
    const nextCursor = dialogs.data?.nextCursor;
    if (!nextCursor) return;
    setCursorStack((current) => [
      ...current.slice(0, pageIndex + 1),
      nextCursor,
    ]);
    setPageIndex((current) => current + 1);
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">История диалогов</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Сообщения доступны только для чтения.
        </p>
      </div>

      <form
        className="panel grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={handleApply}
      >
        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Wazzup-канал</span>
          <select
            className="control"
            value={draft.channelId}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                channelId: event.target.value,
              }))
            }
          >
            <option value="">Все каналы</option>
            {channelOptions.map((channel) => (
              <option key={channel.id} value={channel.id}>
                {channel.id} · {channelTypeLabel(channel.type)}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Контакт / chat ID</span>
          <input
            className="control"
            type="search"
            value={draft.contact}
            placeholder="Имя или ID чата"
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                contact: event.target.value,
              }))
            }
          />
        </label>

        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Дата от</span>
          <input
            className="control"
            type="datetime-local"
            value={draft.from}
            onChange={(event) =>
              setDraft((current) => ({ ...current, from: event.target.value }))
            }
          />
        </label>

        <label className="block text-sm font-medium">
          <span className="mb-1.5 block">Дата до</span>
          <input
            className="control"
            type="datetime-local"
            value={draft.to}
            onChange={(event) =>
              setDraft((current) => ({ ...current, to: event.target.value }))
            }
          />
        </label>

        <div className="flex items-end gap-2">
          <button
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
            type="submit"
            disabled={dialogs.isFetching}
          >
            Применить фильтры
          </button>
          <button
            className="inline-flex h-10 items-center justify-center rounded-md border bg-card px-4 text-sm font-medium disabled:opacity-50"
            type="button"
            disabled={dialogs.isFetching}
            onClick={handleReset}
          >
            Сбросить
          </button>
        </div>

        {validationError ? (
          <p
            className="text-sm text-danger sm:col-span-2 lg:col-span-3"
            role="alert"
          >
            {validationError}
          </p>
        ) : null}
      </form>

      {dialogs.isLoading ? (
        <div className="panel p-4 text-sm text-muted-foreground">
          Загружаем диалоги...
        </div>
      ) : dialogs.error ? (
        <div className="panel p-4 text-sm text-danger">
          {dialogs.error.message}
        </div>
      ) : dialogs.data?.items.length === 0 ? (
        <div className="panel flex items-center gap-3 p-4 text-sm text-muted-foreground">
          <MessageSquareText className="h-5 w-5" />
          Диалоги не найдены.
        </div>
      ) : (
        <div className="space-y-2">
          {(dialogs.data?.items ?? []).map((dialog) => {
            const contactLabel = dialog.contactName ?? dialog.contactId;
            return (
              <Link
                key={dialog.id}
                className="panel block p-4 transition-colors hover:border-primary/40 hover:bg-accent/40"
                to={`/funnels/${funnelId}/dialogs/${dialog.id}`}
                aria-label={`Открыть диалог с ${contactLabel}`}
              >
                <div className="grid gap-3 text-sm lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_100px_minmax(0,2fr)]">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{contactLabel}</p>
                    {dialog.contactName ? (
                      <p className="truncate text-xs text-muted-foreground">
                        {dialog.contactId}
                      </p>
                    ) : null}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Агент</p>
                    <p className="truncate font-medium">{dialog.funnel.name}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Канал</p>
                    <p className="truncate font-medium">{dialog.channelId}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Сообщений</p>
                    <p className="font-medium">
                      {formatTokens(dialog.messagesCount)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    {dialog.lastMessage ? (
                      <>
                        <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
                          <span>
                            {dialog.lastMessage.role === "USER"
                              ? "Клиент"
                              : "AI-агент"}
                          </span>
                          <time dateTime={dialog.lastMessage.createdAt}>
                            {formatDateTime(dialog.lastMessage.createdAt)}
                          </time>
                        </p>
                        <p className="mt-1 line-clamp-2 break-words">
                          {dialog.lastMessage.preview}
                        </p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">Нет сообщений</p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <Pagination
        canGoBack={pageIndex > 0}
        canGoForward={Boolean(dialogs.data?.nextCursor)}
        onBack={() => setPageIndex((current) => Math.max(0, current - 1))}
        onForward={handleForward}
        isLoading={dialogs.isFetching}
      />
    </div>
  );
}
